import { Directive, OnInit, ElementRef, Input, EventEmitter, HostListener, Output } from '@angular/core'
import { Observable } from 'rxjs/Rx'
import { Resource, ResourceType } from '@mprep/shared/data/models'
import { ResourceService } from '@mprep/shared/data/services'

import {
  Utils,
  NavigationService
} from './../'

@Directive({
  selector: '[mpResourceLink]'
})
export class ResourceLinkDirective implements OnInit {

  private _resourceId: string
  private _linkTarget: 'blank'|'self'|'download'|'default'|'void'
  private _resourceObservable: Observable<Resource>

  constructor(
    private _element: ElementRef,
    private _resourceService: ResourceService,
    private _navigationService: NavigationService
  ) {
    this.linkClicked = new EventEmitter<Resource>()
  }

  @Input('mpResourceLink') public set mpResourceLink(value: number|string) {
    const [resourceId, linkTarget] = String(value).split(',')
    this._resourceId = resourceId
    this._linkTarget = ['blank', 'self', 'download', 'default'].includes(linkTarget)
      ? linkTarget as 'blank'|'self'|'download'|'default'|'void'
      : 'default'
  }

  @Output() public linkClicked: EventEmitter<Resource>

  @HostListener('click') public onClick() { this.jsOpenIfNoHref() }

  public ngOnInit(): void {
    if (this.isNonEmptyLink() || typeof this._resourceId === 'undefined' && this._resourceId === '') {
      return
    }

    this._resourceObservable = this._resourceService
      .findModelById(this._resourceId)
      .map((resource: Resource) => {
        if (typeof resource === 'undefined') { throw new Error(`Cannot find resource with ID ${this._resourceId}`) }
        this.setupLinkElement(resource)
        return resource
      })
      .share()

    this._resourceObservable.subscribe()
  }

  private setupLinkElement(resource: Resource) {
    if ([ResourceType.PrintBook, ResourceType.Unknown].includes(resource.resourceType)) { return }
    if (this._linkTarget === 'default' || typeof this._linkTarget === 'undefined') {
      this._linkTarget = this.determineDefaultLinkTarget(resource.resourceType)
    }
    if (this._linkTarget !== 'void') {
      this[`setupLinkElement${Utils.capitalizeFirst(this._linkTarget)}`].call(this, this._element.nativeElement, resource)
    }

    this.addClickEmitter(this._element.nativeElement, resource)
  }

  private determineDefaultLinkTarget(resourceType: ResourceType): 'blank'|'self'|'download'|'void' {
    let linkTarget: 'blank'|'self'|'download'|'void'
    switch (resourceType) {
      case ResourceType.Pdf:
        linkTarget = 'download'
        break
      case ResourceType.BlogArticle:
      case ResourceType.Website:
        linkTarget = 'blank'
        break
      case ResourceType.Cat:
      case ResourceType.Interact:
      case ResourceType.Navigator:
      case ResourceType.Ebook:
      case ResourceType.StudentCenter:
      case ResourceType.Exam:
      case ResourceType.Atlas:
      case ResourceType.Switch:
      case ResourceType.OgExam:
        linkTarget = 'self'
        break
      case ResourceType.PrintBook:
      case ResourceType.Unknown:
      default:
        linkTarget = 'void'
        break
    }
    return linkTarget
  }

  private addClickEmitter(element: HTMLAnchorElement, resource: Resource) {
    element.addEventListener('click', () => {
      this.linkClicked.emit(resource)
    })
  }

  private setupLinkElementDownload(element: HTMLAnchorElement, resource: Resource) {
    element.href = resource.uri
    element.target = '_blank'
  }

  private setupLinkElementBlank(element: HTMLAnchorElement, resource: Resource) {
    element.href = resource.uri
    element.target = '_blank'
  }

  private setupLinkElementSelf(element: HTMLAnchorElement, resource: Resource) {
    element.onclick = () => this._navigationService.navigateToResource(resource)
  }

  private isNonEmptyLink(): boolean {
    return typeof this._element.nativeElement.href === 'string' && this._element.nativeElement.href !== ''
  }

  private jsOpenIfNoHref() {
    if (!this.isNonEmptyLink() && typeof this._resourceObservable !== 'undefined') {
      this._resourceObservable.subscribe(() => {
        this._element.nativeElement.onclick()
      })
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/directives/resource-link.directive.ts